import React, { useMemo, useState, useCallback } from 'react';
import { Row, Col, Tooltip, Dropdown, message, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { hashedColor } from '@helpers/hashedColor';
import {
  getFrameAtTime,
  getSmpteTimecodeAtTime,
  isAudioVideo
} from '@helpers/avInfo';
import { formatDuration } from '@helpers/formatDuration';
import { removeCommentAttachment } from '@redux/actions/mediaViewerAction';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import commentShortTime from '@helpers/commentShortTime';
import commentLongTime from '@helpers/commentLongTime';
import UserAvatar from '@components/UserAvatar';
import { useResponsive } from '@hooks/useResponsive';
import { AssetVersionCommentDto, AttachmentDto } from '@api/Api';
import { htmlSanitize } from '@helpers/htmlSanitize';
import CommentAttachments from '@pages/MediaViewer/Comments/components/CommentAttachments';
import { useDeleteAttachment } from '@hooks/queries/attachment';
import { combineUserName } from '@helpers/combineUserName';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { DEMO_TOUR_MEDIA_VIEWER_COMMENT_ID } from '@config/constants';
import { hasRenderableCommentAnnotations } from '@pages/MediaViewer/helpers/comment-annotations';
import {
  ActionIcon,
  CheckCheckIcon,
  CheckCircleIcon,
  CheckIcon,
  CopySecondaryIcon,
  DeleteIcon,
  DropdownCopyLinkIcon,
  DropdownRenameIcon,
  HighPriorityIcon,
  MarkerIcon,
  SuccessIcon
} from '@icons';
import './CommentItem.less';

const menuItems = [
  {
    label: 'Edit',
    key: 'edit',
    icon: <DropdownRenameIcon />
  },
  {
    label: 'Copy link',
    key: 'copyLink',
    icon: <DropdownCopyLinkIcon />
  },
  {
    label: 'Copy comment text',
    key: 'copyCommentText',
    icon: <CopySecondaryIcon />
  },
  {
    label: 'Turn into task',
    key: 'turnIntoTask',
    icon: <CheckCircleIcon />
  },
  {
    type: 'divider'
  },
  {
    label: 'Delete',
    key: 'delete',
    icon: <DeleteIcon />
  }
];
const disabledActionsDuringLoading = ['edit', 'delete', 'turnIntoTask'];

type CommentItemProps = {
  comment: AssetVersionCommentDto;
  setEditCommentId: (a: string) => void;
  setReplyToCommentId: (a: string | null) => void;
  onDeleteComment: (a: string) => void;
  onTurnCommentIntoTask: (comment: AssetVersionCommentDto) => void;
  onClickComment: () => void;
  canCreateTask: boolean;
  isEmbeddedWebView?: boolean;
  onResolveClick: (comment: AssetVersionCommentDto) => void;
  isSidebarCommentItem?: boolean;
};

function CommentItem({
  comment,
  setEditCommentId,
  setReplyToCommentId,
  onDeleteComment,
  onTurnCommentIntoTask,
  onClickComment,
  onResolveClick,
  canCreateTask,
  isEmbeddedWebView = false,
  isSidebarCommentItem = false
}: CommentItemProps) {
  const { tourData, goToStep } = useOnboardingProcess();
  const { isMdWidth } = useResponsive();
  const canLeaveComment = useAssetMVPermissions('comment');
  const canResolveComment = useAssetMVPermissions('resolveComments');
  const approvalWorkflow = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assets?.asset?.approvalWorkflow
  );
  const {
    mutateAsync: deleteAttachment,
    isPending: isLoadingDeleteAttachment
  } = useDeleteAttachment({});
  const {
    selectedCommentId,
    timeFormat,
    selectedVersion,
    selectComment,
    setMobileSidebarCommentEditing,
    setCanvasMode,
    setDocumentHighlight,
    setIsTimecodeChecked,
    setActivePageNumber,
    setPlayerTime,
    getPlaybackPlayer
  } = useAssetViewerState();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [showMoreActions, setShowMoreActions] = useState<boolean>(false);
  const showAllMenuItems =
    tourData?.type === 'create-task-from-comment' &&
    comment.id === DEMO_TOUR_MEDIA_VIEWER_COMMENT_ID;
  const isOpenMoreActions = showAllMenuItems && goToStep === 1;
  const hasDocumentHighlight = !!comment.documentHighlight?.length;
  const isShowAnnotations =
    (hasRenderableCommentAnnotations(comment, selectedVersion?.metadata.type) &&
      !!comment.annotations?.includes('type')) ||
    hasDocumentHighlight;

  const pageNumber = useMemo(() => {
    return comment.documentPage;
  }, [comment.documentPage]);

  const _menuItems = useMemo(() => {
    const filteredMenuItems = showAllMenuItems
      ? menuItems
      : menuItems.filter((el: any) => {
          const _closeEdit =
            (!comment.permissions.update || !canLeaveComment) &&
            el.key === 'edit';
          const _closeDelete =
            (!comment.permissions.delete || !canLeaveComment) &&
            el.key === 'delete';
          const _closeTurnIntoTask =
            el.key === 'turnIntoTask' &&
            (!canCreateTask || comment.task || isEmbeddedWebView);
          if (_closeEdit || _closeDelete || _closeTurnIntoTask) return false;
          return true;
        });
    const newMenuItems = isMdWidth
      ? [...filteredMenuItems, { label: 'Cancel', key: 'cancel' }]
      : filteredMenuItems;
    return loadingAction
      ? newMenuItems.map((menuItem) => ({
          ...menuItem,
          disabled: disabledActionsDuringLoading.includes(
            menuItem.key as string
          ),
          ...(loadingAction === menuItem.key
            ? {
                itemIcon: <Spin indicator={<LoadingOutlined spin />} />
              }
            : {})
        }))
      : newMenuItems;
  }, [
    canCreateTask,
    isEmbeddedWebView,
    showAllMenuItems,
    canLeaveComment,
    loadingAction,
    comment,
    isMdWidth,
    selectedVersion
  ]);

  const handlerAction = async (key: string) => {
    const hasSearch = !!window.location.search;
    let commentText: string = comment.text || '';
    if (loadingAction && disabledActionsDuringLoading.includes(key)) return;
    switch (key) {
      case 'edit':
        if (comment.timeCode?.fromSeconds) setIsTimecodeChecked(true);
        setEditCommentId(comment.id);
        setReplyToCommentId(null);
        selectComment(comment.id);
        if (isSidebarCommentItem && isMdWidth) {
          setMobileSidebarCommentEditing(true);
        }
        setCanvasMode('edit');
        setDocumentHighlight(
          comment.documentHighlight
            ? {
                text: comment.documentHighlight,
                rects: comment.documentHighlightRects
              }
            : null
        );
        setShowMoreActions(false);
        break;
      case 'copyLink':
        setShowMoreActions(false);
        navigator.clipboard.writeText(
          `${window.location.href}${hasSearch ? '&' : '?'}commentId=${
            comment.id
          }`
        );
        message.success({
          content: 'Link copied',
          className: 'message-dark-modal',
          icon: <SuccessIcon className="anticon" />,
          duration: 1
        });
        break;
      case 'copyCommentText':
        setShowMoreActions(false);
        (comment.mentions || []).forEach((el) => {
          commentText = commentText.replaceAll(
            `{{${el.id}}}`,
            `@${combineUserName(el)}`
          );
        });
        (comment.groupMentions || []).forEach((el) => {
          commentText = commentText.replaceAll(`{{${el.id}}}`, `@${el.name}`);
        });
        commentText = commentText.replaceAll(
          /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
          `@Deleted User`
        );
        navigator.clipboard.writeText(commentText);
        message.success({
          content: 'Comment text copied',
          className: 'message-dark-modal',
          icon: <SuccessIcon className="anticon" />,
          duration: 1
        });
        break;
      case 'turnIntoTask':
        setLoadingAction(key);
        await onTurnCommentIntoTask(comment);
        setLoadingAction(null);
        setShowMoreActions(false);
        break;
      case 'delete':
        setShowMoreActions(false);
        onDeleteComment(comment.id);
        if (comment.id === selectedCommentId) {
          selectComment(null);
          setCanvasMode('view');
        }
        if (isSidebarCommentItem) {
          setMobileSidebarCommentEditing(false);
        }
        break;
      default:
        setShowMoreActions(false);
    }
  };
  const prepareHtml = () => {
    let html: string = comment.text;
    comment.mentions.forEach((el) => {
      html = html.replaceAll(
        `{{${el.id}}}`,
        `<span class="b-suggestion-user">@${combineUserName(el)}</span>`
      );
    });
    comment.groupMentions.forEach((el) => {
      html = html.replaceAll(
        `{{${el.id}}}`,
        `<span class="b-suggestion-user">@${el.name}</span>`
      );
    });
    html = html.replaceAll(
      /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
      `<span class="b-suggestion-user"> 
          @Deleted User
        </span>`
    );
    return html;
  };
  const renderCommentTime = useCallback(
    (pos: string) => {
      const timeCode = comment.timeCode as any;
      if (!timeCode) return '';

      const metadata = selectedVersion?.metadata;
      if (timeFormat === 'frames') {
        const frame = getFrameAtTime(timeCode[pos], metadata);
        if (frame !== null) return frame;
      }
      if (timeFormat === 'timecode') {
        const videoFrames = isAudioVideo(metadata)
          ? metadata.info.avInfo?.video?.frames
          : undefined;
        const avTimecode = isAudioVideo(metadata)
          ? metadata.info.avInfo?.timecode
          : undefined;
        const timecode = getSmpteTimecodeAtTime(
          timeCode[pos],
          avTimecode,
          videoFrames
        );
        if (timecode !== null) return timecode;
      }

      return formatDuration(timeCode[pos]);
    },
    [comment, selectedVersion?.metadata, timeFormat]
  );

  const onNavigateToTimeOrPage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (comment.isCopied) return;
      onClickComment();

      if (pageNumber != null) {
        setActivePageNumber(pageNumber);
      }

      const { timeCode } = comment;
      const fromSeconds = timeCode?.fromSeconds;
      if (timeCode && typeof fromSeconds === 'number') {
        const isRange = timeCode.toSeconds != null && timeCode.toSeconds >= 0;
        if (isRange) {
          setPlayerTime([fromSeconds, timeCode.toSeconds ?? 0]);
        } else {
          setPlayerTime([fromSeconds, null]);
        }

        const player = getPlaybackPlayer();
        if (player) {
          player.currentTime(fromSeconds);
          player.pause();
        }
      }
    },
    [
      comment.timeCode,
      getPlaybackPlayer,
      onClickComment,
      pageNumber,
      setActivePageNumber,
      setPlayerTime
    ]
  );

  const onCopyTextHighlight = useCallback(() => {
    navigator.clipboard.writeText(comment.documentHighlightReplacement ?? '');
    requestAnimationFrame(() => {
      message.success({
        content: 'Copied text to clipboard',
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />
      });
    });
  }, [comment.documentHighlightReplacement]);

  const onDeleteFile = useCallback(
    async (file: AttachmentDto) => {
      if (isLoadingDeleteAttachment) return;
      if (!file.id) return;
      try {
        await deleteAttachment({
          id: file.id
        });
        await removeCommentAttachment({
          commentId: comment.id,
          attachmentId: file.id
        });
      } catch (e: any) {
        const errorText =
          e.description || e?.response?.data?.message || 'Something went wrong';
        message.error(errorText);
      }
    },
    [isLoadingDeleteAttachment, comment, deleteAttachment]
  );

  return (
    <div
      className={classNames(
        'media_viewer_comment_item',
        !!comment.resolvedAt && 'b-resolved'
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClickComment();
      }}
    >
      <div className="media_viewer_comment_item-top">
        <Row wrap={false} gutter={12} align="middle">
          <Col>
            <div
              className="media_viewer_comment_item-avatar-container"
              style={{
                borderColor: hashedColor(
                  comment?.user?.id || '',
                  'mediaViewerComment'
                )
              }}
            >
              <UserAvatar
                className="media_viewer_comment_item-avatar"
                isActive={true}
                size={28}
                src={comment?.user?.picture.url || ''}
                userEmail={comment?.user?.email || ''}
                userName={combineUserName(comment.user)}
              />
            </div>
          </Col>
          <Col>
            <Row gutter={8} align="middle">
              <Col flex="auto" className="media_viewer_comment_item-name">
                <Tooltip
                  placement="top"
                  title={combineUserName(comment.user)}
                  overlayClassName="media_viewer_tooltip"
                >
                  {combineUserName(comment.user)}
                </Tooltip>
              </Col>
              {!!approvalWorkflow && comment.priority && (
                <Col>
                  <Tooltip
                    placement="top"
                    title="High priority"
                    overlayClassName="media_viewer_tooltip"
                  >
                    <HighPriorityIcon style={{ display: 'block' }} />
                  </Tooltip>
                </Col>
              )}
              {comment.isExternal && (
                <Col>
                  <div className="media_viewer_comment_item-label">
                    External
                  </div>
                </Col>
              )}
              <Tooltip
                placement="top"
                overlayClassName="media_viewer_tooltip"
                title={
                  <>{commentLongTime(comment.updatedAt || comment.createdAt)}</>
                }
              >
                <Col className="media_viewer_comment_item-date">
                  {commentShortTime(comment.updatedAt || comment.createdAt)}
                </Col>
              </Tooltip>
              <Col className="media_viewer_comment_item-date" />
            </Row>
          </Col>
          <Col flex="auto">
            <Row justify="end" wrap={false} align="middle" gutter={12}>
              {canResolveComment && (
                <Col>
                  <Tooltip
                    placement="bottom"
                    overlayClassName="media_viewer_tooltip media_viewer_tooltip-small media_viewer_tooltip-no-border"
                    title={
                      comment.resolvedAt
                        ? 'Mark as unresolved'
                        : 'Mark as resolved'
                    }
                  >
                    <div
                      className="media_viewer_comment_item-resolve"
                      onClick={(e) => {
                        e.stopPropagation();
                        onResolveClick(comment);
                      }}
                    >
                      {!comment.resolvedAt && <CheckIcon />}
                      {!!comment.resolvedAt && <CheckCheckIcon />}
                    </div>
                  </Tooltip>
                </Col>
              )}
              <Col>
                <Dropdown
                  menu={{
                    selectable: false,
                    items: _menuItems as any,
                    onClick: ({ key, domEvent }: any) => {
                      domEvent.stopPropagation();
                      handlerAction(key);
                    }
                  }}
                  prefixCls="media_viewer_menu"
                  overlayClassName="media_viewer_comment_item-overlay media_viewer_mobile-overlay media_viewer_comment_item-menu tour-create-task-from-comment-menu"
                  placement="bottomRight"
                  trigger={['click']}
                  open={showMoreActions || isOpenMoreActions}
                  onOpenChange={(v) => setShowMoreActions(v)}
                >
                  <Tooltip
                    placement="bottom"
                    overlayClassName="media_viewer_tooltip media_viewer_tooltip-small media_viewer_tooltip-no-border"
                    title="More options"
                    zIndex={100}
                  >
                    <ActionIcon
                      onClick={(e: any) => e.stopPropagation()}
                      className="media_viewer_comment_item-action"
                    />
                  </Tooltip>
                </Dropdown>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      {!!comment.documentHighlightDiff && (
        <div className="media_viewer_comment_item_text-highlight">
          <div
            className="media_viewer_comment_item_text-highlight-inner"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: htmlSanitize(
                comment.documentHighlightDiff.replace(/&para;|¶/g, '')
              ) as string
            }}
          />
          <Tooltip
            placement="topRight"
            overlayClassName="media_viewer_tooltip"
            title="Copy edited comment"
          >
            <div
              onClick={onCopyTextHighlight}
              className="media_viewer_comment_item_text-highlight-icon"
            >
              <CopySecondaryIcon />
            </div>
          </Tooltip>
        </div>
      )}
      <div className="media_viewer_comment_item-message">
        {(comment.timeCode || pageNumber) && (
          <Tooltip
            placement="top"
            overlayClassName="media_viewer_tooltip media_viewer_tooltip-small media_viewer_tooltip-no-border"
            title="Cannot be displayed on this asset"
            trigger={!isMdWidth && comment.isCopied ? ['hover'] : []}
          >
            <div
              className="media_viewer_comment_item-time"
              onClick={onNavigateToTimeOrPage}
            >
              {comment.timeCode && (
                <>
                  {renderCommentTime('fromSeconds')}
                  {comment.timeCode.toSeconds && (
                    <> - {renderCommentTime('toSeconds')}</>
                  )}
                </>
              )}
              {pageNumber && <>Page {pageNumber}</>}
            </div>
          </Tooltip>
        )}

        {isShowAnnotations && (
          <Tooltip
            overlayClassName="media_viewer_tooltip media_viewer_tooltip-small media_viewer_tooltip-no-border"
            title={
              comment.isCopied
                ? 'Cannot be displayed on this asset'
                : 'Go to comment'
            }
            placement="bottom"
          >
            <div className="media_viewer_comment_item-annotations">
              <MarkerIcon />
            </div>
          </Tooltip>
        )}

        <div
          className="media_viewer_comment_item-message-text"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: htmlSanitize(prepareHtml()) as any
          }}
        />
      </div>
      {!!comment.attachments?.length && (
        <CommentAttachments
          isEmbeddedWebView={isEmbeddedWebView}
          files={comment.attachments}
          mode={comment.user?.me ? 'edit' : 'view'}
          onDeleteFile={onDeleteFile}
        />
      )}
    </div>
  );
}

export default CommentItem;
